import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Input, Popconfirm, Spin } from 'antd';
import api from 'api';
import { UserContext } from 'contexts/User';
import Privileges from 'lib/constants/privileges';
import { userHasPrivileges } from 'lib/helpers/renderCondition';
import { formFromRecord, locationSummary, validate } from './form';
import './style.css';

const FIELDS = [
    {key: 'address', label: 'Street Address'},
    {key: 'address2', label: 'Apt / Unit'},
    {key: 'city', label: 'City'},
    {key: 'state', label: 'State'},
    {key: 'zipcode', label: 'Zip Code'},
    {key: 'latitude', label: 'Latitude', editOnly: true},
    {key: 'longitude', label: 'Longitude', editOnly: true},
];

function UserHomeAddress({ user_id }) {
    const { state: { user: currentUser } } = useContext(UserContext);
    const canEdit = userHasPrivileges([Privileges.EDIT_USER_HOME_ADDRESSES], currentUser);

    const [record, setRecord] = useState(null);
    const [form, setForm] = useState(formFromRecord(null));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);

    const applyRecord = (next) => {
        setRecord(next);
        setForm(formFromRecord(next));
    };

    useEffect(() => {
        let stale = false;
        api.get('/user_home_addresses', {user_id}).then(response => {
            if(stale) {
                return;
            }
            if(response.success) {
                applyRecord(response.body[0] || null);
            } else {
                setError("Couldn't load the home address.");
            }
            setLoading(false);
        });

        return () => { stale = true; };
    }, [user_id]);

    const change = (key, value) => {
        setSaved(false);
        setForm({...form, [key]: value});
    };

    const save = async () => {
        const problem = validate(form);
        setError(problem);
        setSaved(false);
        if(problem) {
            return;
        }

        setSaving(true);
        const response = await api.upsert(`/user_home_addresses/${user_id}`, form);
        setSaving(false);

        if(response.success) {
            applyRecord(response.body);
            setSaved(true);
        } else {
            setError("Couldn't save the home address.");
        }
    };

    const remove = async () => {
        setSaving(true);
        const response = await api.destroy(`/user_home_addresses/${user_id}`);
        setSaving(false);

        if(response.success) {
            applyRecord(null);
            setSaved(false);
            setError(null);
        } else {
            setError("Couldn't remove the home address.");
        }
    };

    if(loading) {
        return <div className="user-home-address"><Spin size="small" /></div>;
    }

    return <div className="user-home-address">
        <h3>Home Address</h3>

        {error ? <Alert type="error" message={error} showIcon className="user-home-address__alert" /> : null}
        {saved ? <Alert type="success" message="Home address saved." showIcon className="user-home-address__alert" /> : null}

        {canEdit || record ? FIELDS.filter(field => canEdit || !field.editOnly).map(({key, label}) =>
            <label className="user-home-address__field" key={key}>
                <span>{label}</span>
                {canEdit
                    ? <Input value={form[key]} onChange={e => change(key, e.target.value)} />
                    : <div>{form[key] || '—'}</div>}
            </label>
        ) : <p>No home address on file.</p>}

        {record ? <p className="user-home-address__location">{locationSummary(record)}</p> : null}

        {canEdit ? <div className="user-home-address__actions">
            <Button type="primary" loading={saving} onClick={save}>Save Home Address</Button>
            {record ? <Popconfirm title="Remove this home address?" onConfirm={remove}>
                <Button disabled={saving}>Remove</Button>
            </Popconfirm> : null}
        </div> : null}
    </div>;
}

export default UserHomeAddress;
